import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { green, lightBlue } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
  useActivityIntervalOptionsQuery,
  useDomainOptionsQuery,
  useVocationalActivitiesOptionsQuery
} from '../../../hooks/useOptions';
import {
  generateVcmUnits,
  useLastApprovedVcmQuery,
  usePointRequestQuery,
  useUser
} from '../../../hooks/useUser';
import ApproveModal from '../../users/user-point-request/ApproveModal';
import ConvertModal from '../../users/user-point-request/ConvertModal';
import EditModal from '../../users/user-point-request/EditModal';
import RejectModal from '../../users/user-point-request/RejectModal';
import { ExternalCurrencyDetails } from './ExternalCurrencyDetails';

const ExternalCurrencyModal = ({
  open,
  onClose,
  externalCurrency,
  institute
}) => {
  const [isEditModalOpened, setIsEditModalOpened] = React.useState(false);
  const [isApproveModalOpened, setIsApproveModalOpened] = React.useState(false);
  const [isRejectModalOpened, setIsRejectModalOpened] = React.useState(false);
  const [isConvertModalOpened, setIsConvertModalOpened] = React.useState(false);

  const queryClient = useQueryClient();
  const { data: pointRequest } = usePointRequestQuery({
    userId: externalCurrency?.user?.id,
    id: externalCurrency?.id
  });

  const { data: domainOptions } = useDomainOptionsQuery();
  const { data: vocationalActivitiesOptions } = useVocationalActivitiesOptionsQuery();
  const { data: activityIntervalOptions } = useActivityIntervalOptionsQuery();
  const { data: lastApprovedVcm } = useLastApprovedVcmQuery(externalCurrency?.user.id);

  const { user } = useUser();

  const vcmUnits = React.useMemo(() => {
    if (!lastApprovedVcm) return [];
    return generateVcmUnits(lastApprovedVcm);
  }, [lastApprovedVcm]);

  const vocationalCurrencyUsesUnits = React.useMemo(() => {
    return institute?.vocational_currency_uses_units;
  }, [institute]);

  const pointsFileAttachmentEnabled = React.useMemo(() => {
    return institute?.points_file_attachment_enabled;
  }, [institute]);

  const allowSupervisorToActionOn = React.useMemo(() => {
    // Stop supervisors from approving/rejecting their own EPC-PR and V-PR
    if (user?.is_any_supervisor && externalCurrency?.user.id === user?.id) {
      console.log('Supervisor cannot action on their own requests');
      return false;
    }

    // Add null check for allow_supervisor_to_action
    const allowSupervisorToAction = institute?.allow_supervisor_to_action;
    if (!allowSupervisorToAction || !Array.isArray(allowSupervisorToAction)) {
      return false;
    }

    const canActionReaPointRequest =
      pointRequest?.type === 'ReaPointRequest' &&
      allowSupervisorToAction.includes('external_professional_currency_point_requests');

    const canActionVocationalPointRequest =
      pointRequest?.type === 'VocationalPointRequest' &&
      allowSupervisorToAction.includes('vocational_point_requests');

    return (canActionReaPointRequest || canActionVocationalPointRequest);
  }, [pointRequest, institute, user]);

  const openApproveModal = React.useCallback(() => {
    setIsApproveModalOpened(pointRequest !== undefined);
  }, [pointRequest]);

  const openRejectModal = React.useCallback(() => {
    setIsRejectModalOpened(pointRequest !== undefined);
  }, [pointRequest]);

  const openConvertModal = React.useCallback(() => {
    setIsConvertModalOpened(pointRequest !== undefined);
  }, [pointRequest]);

  const openEditModal = React.useCallback(() => {
    setIsEditModalOpened(pointRequest !== undefined);
  }, [pointRequest]);

  const closeApproveModal = React.useCallback(() => {
    setIsApproveModalOpened(false);
  }, []);

  const closeRejectModal = React.useCallback(() => {
    setIsRejectModalOpened(false);
  }, []);

  const closeConvertModal = React.useCallback(() => {
    setIsConvertModalOpened(false);
  }, []);

  const closeEditModal = React.useCallback(() => {
    setIsEditModalOpened(false);
  }, []);

  const handleRefresh = React.useCallback(() => {
    queryClient.invalidateQueries(['externalCurrencyRequests']);
    onClose();
  }, [queryClient, onClose]);

  const isWarning = React.useMemo(
    () =>
      externalCurrency?.overdueDays > institute?.point_request_warning_days &&
      externalCurrency?.overdueDays <= institute?.point_request_overdue_days,
    [externalCurrency, institute]
  );
  const showOverdue = React.useMemo(
    () => externalCurrency?.overdueDays > institute?.point_request_warning_days,
    [externalCurrency, institute]
  );

  if (!externalCurrency) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#1565c0'
          }}
        >
          Action External Currency Requests
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showOverdue && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`Overdue ${externalCurrency.overdueDays} days`}
                size="small"
                style={{
                  backgroundColor: isWarning ? '#FFB300' : '#E53935',
                  color: 'white'
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" component="span">
                <strong>Requested:</strong>{' '}
              </Typography>
              <Typography variant="body2" component="span">
                {new Date(externalCurrency.created_at).toLocaleDateString('en-AU')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" component="span">
                <strong>Educator:</strong>{' '}
              </Typography>
              <Typography variant="body2" component="span">
                {externalCurrency.user?.full_name}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" component="span">
                <strong>Type:</strong>{' '}
              </Typography>
              <Typography variant="body2" component="span">
                {externalCurrency.type_label}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Activity details
          </Typography>
          <ExternalCurrencyDetails
            externalCurrency={externalCurrency}
            openEditModal={openEditModal}
            allowEditModal={user?.is_any_admin || allowSupervisorToActionOn}
          />

          {user?.is_any_admin || user.is_any_supervisor ? (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 3
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={openConvertModal}
                style={{ backgroundColor: lightBlue[600] }}
              >
                CONVERT
              </Button>
              {user?.is_any_admin || allowSupervisorToActionOn ? (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={openApproveModal}
                    style={{ backgroundColor: green[600] }}
                  >
                    APPROVE
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={openRejectModal}
                  >
                    INCOMPLETE
                  </Button>
                </>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
      <ApproveModal
        isOpened={isApproveModalOpened}
        onClose={closeApproveModal}
        pointRequest={pointRequest}
        onRefreshRequest={handleRefresh}
        user={externalCurrency?.user}
      />
      <RejectModal
        isOpened={isRejectModalOpened}
        onClose={closeRejectModal}
        pointRequest={pointRequest}
        onRefreshRequest={handleRefresh}
        user={externalCurrency?.user}
      />
      <ConvertModal
        isOpened={isConvertModalOpened}
        onClose={closeConvertModal}
        pointRequest={pointRequest}
        onRefreshRequest={handleRefresh}
        user={externalCurrency?.user}
      />
      <EditModal
        isOpened={isEditModalOpened}
        onClose={closeEditModal}
        pointRequest={externalCurrency}
        onRefreshRequest={handleRefresh}
        domainOptions={domainOptions}
        vocationalActivitiesOptions={vocationalActivitiesOptions}
        activityIntervalOptions={activityIntervalOptions}
        pointsFileAttachmentEnabled={pointsFileAttachmentEnabled}
        user={externalCurrency?.user}
        vocationalCurrencyUsesUnits={vocationalCurrencyUsesUnits}
        vcmUnits={vcmUnits}
      />
    </>
  );
};

export default ExternalCurrencyModal;
